import { useState } from 'react';
import { useInstanceStore } from '../../stores/UseInstanceStore';

const EMAIL = 'kitty@katbot.ai';

function EmailDialog({ onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(EMAIL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Email Kitty</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-slate-400 leading-relaxed">
          Send an email expressing your interest in the INCITERS co-heart. Include your wallet address so we can add you directly.
        </p>

        <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5">
          <span className="flex-1 text-sm font-mono text-cyan-300 select-all">{EMAIL}</span>
          <button
            onClick={handleCopy}
            className="text-xs text-slate-400 hover:text-white transition-colors whitespace-nowrap"
          >
            {copied ? '✓ copied' : 'copy'}
          </button>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Include in your email</p>
          <ul className="text-xs text-slate-400 space-y-1">
            <li className="flex items-start gap-1.5"><span className="text-cyan-500 mt-0.5">·</span> Your wallet address</li>
            <li className="flex items-start gap-1.5"><span className="text-cyan-500 mt-0.5">·</span> What you trade and your experience level</li>
            <li className="flex items-start gap-1.5"><span className="text-cyan-500 mt-0.5">·</span> Subject: <span className="text-slate-300 font-mono">INCITERS Early Access</span></li>
          </ul>
        </div>

        <a
          href={`mailto:${EMAIL}?subject=INCITERS%20Early%20Access`}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-medium rounded-lg transition-all duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Open in mail app
        </a>
      </div>
    </div>
  );
}

export function IncitersBanner() {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const userSub = useInstanceStore((state) => state.instance.userSub);
  const declinedSubs = useInstanceStore((state) => state.instance.incitersCtaDeclinedSubs);
  const declineIncitersCtaForSub = useInstanceStore((state) => state.declineIncitersCtaForSub);

  const incitersCtaDeclined = !!(userSub && (declinedSubs || []).includes(userSub.toLowerCase()));

  if (incitersCtaDeclined) return null;

  return (
    <>
    {emailDialogOpen && <EmailDialog onClose={() => setEmailDialogOpen(false)} />}
    <div className="bg-green-800/70 border border-green-400/80 rounded-lg p-5">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded bg-green-700 text-white font-bold tracking-wide">
              EARLY ACCESS OFFER
            </span>
            <h3 className="text-base font-semibold text-white">
              Join the INCITERS Early Access Co-Heart
            </h3>
          </div>
          <p className="text-sm text-gray-300 leading-relaxed">
            Shape Katbot's roadmap and get direct access to the developer team.{' '}
            <span className="text-cyan-300 font-medium">
              INCITERS members receive a free subscription
            </span>{' '}
            for the duration of Early Access — limited spots available.
          </p>
          <div className="flex flex-wrap gap-3 pt-1">
            <a
              href="https://discord.gg/asTcxUTzRv"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white text-sm font-medium rounded-lg transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.317 4.3671a19.8062 19.8062 0 00-4.8850-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2510-1.8447-.2762-3.6879-.2762-5.4979 0-.1636-.3933-.3972-.8757-.6115-1.2510a.077.077 0 00-.0785-.037 19.7354 19.7354 0 00-4.8854 1.515.0699.0699 0 00-.0321.0277C1.6747 9.6964 1.0760 14.7192 2.4798 19.5835a.0803.0803 0 00.0314.0553 19.902 19.902 0 005.9856 3.0009.0800.0800 0 00.0842-.0330c.4625-.8250.8765-1.6960 1.2283-2.5970a.0777.0777 0 00-.0422-.0868 13.035 13.035 0 01-1.8637-.892.0771.0771 0 01-.0076-.1276c.1249-.094.2497-.1952.3709-.3018a.0760.0760 0 01.0776-.0123c3.9102 1.7919 8.1960 1.7919 12.0614 0a.076.076 0 01.0776.0122c.1212.1066.246.2078.3711.3018a.077.077 0 01-.0075.1276 12.964 12.964 0 01-1.8694.8924.0772.0772 0 00-.0421.0868c.3507.9015.7655 1.7728 1.2284 2.5971a.0800.0800 0 00.0842.0330 19.89 19.89 0 005.9849-3.0009.0801.0801 0 00.0314-.0553c1.4798-4.9503.7836-9.2433-1.1157-12.8749a.0699.0699 0 00-.0321-.0277zM8.02 15.3312c-1.1825 0-2.1569-.9718-2.1569-2.1575 0-1.1858.9518-2.1575 2.1569-2.1575 1.2051 0 2.1755.9717 2.1568 2.1575 0 1.1857-.9518 2.1575-2.1568 2.1575zm7.9581 0c-1.1825 0-2.1569-.9718-2.1569-2.1575 0-1.1858.9518-2.1575 2.1569-2.1575 1.2051 0 2.1755.9717 2.1568 2.1575 0 1.1857-.9517 2.1575-2.1568 2.1575z" />
              </svg>
              Join Discord
            </a>
            <button
              onClick={() => setEmailDialogOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-medium rounded-lg transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Kitty
            </button>
          </div>
        </div>
        <button
          onClick={() => declineIncitersCtaForSub(userSub)}
          disabled={!userSub}
          className="self-start text-xs text-slate-300 hover:text-slate-300 transition-colors whitespace-nowrap pt-0.5 disabled:opacity-40"
        >
          Decline this offer
        </button>
      </div>
    </div>
    </>
  );
}
